import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Link, Rating } from '@resy/ui-kitchen';
import pinSVG from '../../../../../../../ui-kitchen/src/svg/icons/ic_location_20x20.svg';
import Info from '../../../../../../../ui-kitchen/src/svg/icons/ic_info-outline_20x20.svg';
import IconCalendarDay from '../../../../../../../ui-kitchen/src/components/IconCalendarDay';
import { parameterize, priceRangeToString } from '../../../../../../../ui-kitchen/src/utils';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import { COMING_SOON_STATUS, NOTIFY_STATUS } from '../../constants';
import { handleMktAnalytics } from '../../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../../AmexMarketingUtils/constants';
import AmexPlatinumReservationButtonListRefresh from '../AmexPlatinumReservationButtonListRefresh';
import './AmexPlatinumNightsCard.scss';

const AmexPlatinumNightsCard = (
  {
    Analytics,
    restaurant = {},
    index,
    loadNotifyModal,
    showAuthModal,
    comingSoonLabel,
    bookLabel,
    notifyLabel,
    matchMedia,
  }
) => {
  if (Object.keys(restaurant).length === 0) return null;

  const {
    cuisine,
    locationName,
    venueName,
    venueId,
    image,
    status,
    date,
    locationSlug,
    venueSlug,
  } = restaurant;
  const comingSoonStatus = status === COMING_SOON_STATUS;
  const notifyStatus = status === NOTIFY_STATUS;

  const hideNotify = notifyStatus && restaurant?.isNotifyHidden;

  let bookingBricksLimit = 2;
  if (matchMedia?.lg) bookingBricksLimit = 3;
  if (matchMedia?.xl) bookingBricksLimit = 4;

  const renderButtons = () => (
    <AmexPlatinumReservationButtonListRefresh
      ariaDescribedBy={`cardTitle${index}`}
      activeNotifications={restaurant.activeNotifications}
      Analytics={Analytics}
      featureRecaptcha={restaurant.featureRecaptcha}
      reservations={restaurant.slots}
      venueId={restaurant.venueId}
      venueName={venueName}
      limit={bookingBricksLimit}
      limitSlots
      selectedDate={restaurant.date}
      selectedSeats={restaurant.partySize}
      loadNotifyModal={loadNotifyModal}
      availableServiceTypes={restaurant.availableServiceTypes}
      showAuthModal={showAuthModal}
    />
  );

  const query = {
    date: restaurant.date,
    seats: restaurant.partySize,
  };

  const getVenueLink = () => `cities/${locationSlug}/venues/${venueSlug}?${parameterize(query)}`;

  const renderLabel = () => {
    if (comingSoonStatus) return comingSoonLabel;
    if (notifyStatus) return notifyLabel;
    return bookLabel;
  };

  const sanitizedInfoString = `${renderLabel()}:  ${sanitizeString(restaurant?.revervationInfo)}`;
  const sanitizedNotifyMessage = sanitizeString(restaurant?.hideNotifyMessage);
  const sanitizedVenueInfo = sanitizeString(restaurant?.venueInfo);
  const price = restaurant?.priceRange ? priceRangeToString(restaurant?.priceRange, restaurant?.currency) : null;

  return (
    <div className="AmexPlatinumNightsCard" role="group">
      <div
        className="AmexPlatinumNightsCard__image"
        style={{
          backgroundImage: `url(${image})`,
        }}
      >
        <time dateTime={date} className="calendar-icon">
          <IconCalendarDay date={date} />
        </time>
      </div>
      <div className="AmexPlatinumNightsCard__content">
        <h3
          id={`cardTitle${index}`}
          className="AmexPlatinumNightsCard__content--name"
        >
          <Link
            className="venue-name"
            target="_blank"
            rel="noopener noreferrer"
            href={getVenueLink()}
            key={`${venueName}-${venueId}`}
            aria-label={venueName}
            onClick={() => {
              if (Analytics.event) {
                const extraPrams = {
                  action_name: 'Platinum Nights Venue Click',
                  item_name: venueName,
                };
                handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
              }
            }}
          >
            {venueName}
          </Link>
        </h3>
        <div>
          <p className="AmexPlatinumNightsCard__content--info">
            <Rating
              scale={5}
              score={restaurant?.rating}
              soldout={restaurant?.slots.length === 0}
            />
            <span className="cuisine">
              {cuisine}
            </span>
            { price && (
              <span className="price-range">
                {price}
              </span>
            )}
          </p>
          <p className="AmexPlatinumNightsCard__content--location">
            <Icon component={pinSVG} name="location-pin" ariaLabel="Location " />
            {locationName}
          </p>
        </div>
        {restaurant?.venueInfo && (
          <div className="AmexPlatinumNightsCard__content--description">
            <p>
              <span aria-hidden="true">
                <Icon component={Info} name="icon-info" />
              </span>
              <span
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizedVenueInfo,
                }}
              />
            </p>
          </div>
        )}
        <div className="AmexPlatinumNightsCard__content--bottom">
          {(restaurant?.revervationInfo && comingSoonStatus) && (
            <p
              className="AmexPlatinumNightsCard__footer"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizeString(sanitizedInfoString),
              }}
            />
          )}
          {!hideNotify && !comingSoonStatus && renderButtons()}
          {(notifyStatus && restaurant?.isNotifyHidden && restaurant?.hideNotifyMessage) && (
            <p
              className="AmexPlatinumNightsCard__footer"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedNotifyMessage,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default AmexPlatinumNightsCard;

AmexPlatinumNightsCard.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  index: PropTypes.number.isRequired,
  restaurant: PropTypes.shape({}).isRequired,
  loadNotifyModal: PropTypes.func.isRequired,
  showAuthModal: PropTypes.func.isRequired,
  comingSoonLabel: PropTypes.string.isRequired,
  bookLabel: PropTypes.string.isRequired,
  notifyLabel: PropTypes.string.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};
